// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { Link } from '@/components/link';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useToast } from '@/contexts/toast-context';
import { createFulfillmentOrder } from '@/fulfillment/api/create-fulfillment-order';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { updateFulfillmentOrder } from '@/fulfillment/api/update-fulfillment-order';
import { FulfillmentOrderForm } from '@/fulfillment/components/fulfillment-order-form';
import { useFulfillmentFileUploads } from '@/fulfillment/hooks/use-fulfillment-file-uploads';
import {
  fulfillmentDetailQueryKey,
  fulfillmentsQueryKey,
} from '@/fulfillment/query-keys';
import { FulfillmentOrderFormData } from '@/fulfillment/schemas/fulfillment-order-form-schema';
import { projectFulfillmentBySkuQueryKey } from '@/project/hooks/use-project-fulfillment-by-sku';
import { fulfillmentBySkuReportBaseQueryKey } from '@/report/hooks/use-fulfillment-by-sku-report';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useCallback, useState } from 'react';

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;

  /**
   * Optional fulfillment order to edit. If provided, the dialog will be in edit mode.
   */
  fulfillment?: FulfillmentOrder;

  /**
   * Optional initial project ID to set the project field.
   */
  initialProjectID?: string;

  /**
   * Optional initial form data to pre-populate the form.
   * Used for cloning a fulfillment order with pre-filled values.
   */
  initialFormData?: FulfillmentOrderFormData;

  /**
   * Optional items to seed the manifest with on a new order. Unlike
   * `initialFormData`, other fields still get their defaults (such as the
   * organization default origin location).
   */
  preFillItems?: FulfillmentOrderFormData['items'];
}

const FORM_ID = 'fulfillment-order-form';

/**
 * Dialog for creating or editing a fulfillment order.
 * Handles mutations, error display, and success notifications.
 */
export function FulfillmentOrderDialog(props: Props) {
  const [formVersion, setFormVersion] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProjectContextBlocked, setIsProjectContextBlocked] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { user } = useCurrentUser();
  const isEditMode = !!props.fulfillment;
  const fileUploads = useFulfillmentFileUploads();

  const createMutation = useMutation({
    mutationFn: createFulfillmentOrder,
    onSuccess: (fulfillment) => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentsQueryKey,
        refetchType: 'active',
      });
      // The new FO changes project demand coverage. Invalidate the project's
      // fulfillment-by-SKU summary so subsequent dialog opens recompute Need.
      if (fulfillment.project) {
        queryClient.invalidateQueries({
          queryKey: projectFulfillmentBySkuQueryKey(fulfillment.project.key),
        });
      }
      // The new FO also changes the org-wide fulfillment-by-SKU report, so a
      // line fulfilled straight from that report reflects the updated need
      // without a manual reload.
      queryClient.invalidateQueries({
        queryKey: fulfillmentBySkuReportBaseQueryKey,
      });
      showToast({
        element: (
          <Typography>
            Fulfillment order{' '}
            <Link
              to="/fulfillments/$fulfillmentKey"
              params={{ fulfillmentKey: fulfillment.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {fulfillment.fulfillmentOrderNumber}
            </Link>{' '}
            created
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (apiError.apiStatusCode > 0) {
        setErrorMessage(apiError.message);
        setShowSnackbar(false);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(null);
        setShowSnackbar(true);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      fulfillmentKey,
      request,
    }: {
      fulfillmentKey: string;
      request: Parameters<typeof updateFulfillmentOrder>[1];
    }) => updateFulfillmentOrder(fulfillmentKey, request),
    onSuccess: (updatedFulfillment, variables) => {
      queryClient.setQueryData(
        fulfillmentDetailQueryKey(variables.fulfillmentKey),
        updatedFulfillment
      );
      queryClient.invalidateQueries({
        queryKey: fulfillmentsQueryKey,
        refetchType: 'active',
      });
      // Invalidate the by-SKU summary for both the previous and new project
      // (in case the user reassigned the FO across projects mid-edit) so the
      // next dialog open or summary card visit reflects the change.
      const projectKeys = new Set<string>();
      if (props.fulfillment?.project) {
        projectKeys.add(props.fulfillment.project.key);
      }
      if (updatedFulfillment.project) {
        projectKeys.add(updatedFulfillment.project.key);
      }
      for (const key of projectKeys) {
        queryClient.invalidateQueries({
          queryKey: projectFulfillmentBySkuQueryKey(key),
        });
      }
      // Editing an FO also shifts coverage on the org-wide report.
      queryClient.invalidateQueries({
        queryKey: fulfillmentBySkuReportBaseQueryKey,
      });
      showToast({
        element: (
          <Typography>
            Fulfillment order{' '}
            <Link
              to="/fulfillments/$fulfillmentKey"
              params={{ fulfillmentKey: updatedFulfillment.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {updatedFulfillment.fulfillmentOrderNumber}
            </Link>{' '}
            updated
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (apiError.apiStatusCode > 0) {
        setErrorMessage(apiError.message);
        setShowSnackbar(false);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(null);
        setShowSnackbar(true);
      }
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  // File upload state and a still-loading project context affect the submit
  // button. The latter only fires for the clone (create) path; edit mode never
  // reports loading.
  const canSubmit =
    !isPending &&
    !isProjectContextBlocked &&
    !fileUploads.isAnyUploading &&
    !fileUploads.hasFailedUploads;

  const handleAfterClose = useCallback(() => {
    setFormVersion((v) => v + 1);
    setErrorMessage(null);
    setShowSnackbar(false);
    setIsProjectContextBlocked(false);
    createMutation.reset();
    updateMutation.reset();
    fileUploads.reset();
  }, [createMutation, updateMutation, fileUploads]);

  const handleFormSubmit = useCallback(
    (data: FulfillmentOrderFormData) => {
      const items = data.items.map((item) => ({
        itemId: item.item?.id ?? '',
        requestedQuantity: item.quantity ?? 0,
      }));

      if (isEditMode && props.fulfillment) {
        const updateRequest = {
          projectId: data.project?.id ?? null,
          fromLocationId: data.fromLocation?.id ?? '',
          toLocationId: data.toLocation?.id ?? '',
          promiseShipDate: data.promiseShipDate,
          promiseDeliveryDate: data.promiseDeliveryDate,
          orderNotes: data.orderNotes,
          contactId: data.contact?.id ?? null,
          items,
        };

        updateMutation.mutate({
          fulfillmentKey: props.fulfillment.key,
          request: updateRequest,
        });
        return;
      }

      const createRequest = {
        projectId: data.project?.id ?? null,
        fromLocationId: data.fromLocation?.id ?? '',
        toLocationId: data.toLocation?.id ?? '',
        promiseShipDate: data.promiseShipDate,
        promiseDeliveryDate: data.promiseDeliveryDate,
        orderNotes: data.orderNotes,
        contactId: data.contact?.id ?? null,
        items,
        fileIds: fileUploads.getCompletedFileIds(),
      };

      createMutation.mutate(createRequest);
    },
    [isEditMode, props.fulfillment, createMutation, updateMutation, fileUploads]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={props.isOpen}
      onClose={props.onClose}
      scroll="paper"
      TransitionProps={{
        onExited: handleAfterClose,
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <span>
            {isEditMode ? 'Edit fulfillment order' : 'Add fulfillment order'}
          </span>
          {isEditMode && props.fulfillment?.exchangeGroup && (
            <Typography variant="body2" color="text.secondary">
              Part of exchange{' '}
              <b>{props.fulfillment.exchangeGroup.exchangeNumber}</b>
              {props.fulfillment.exchangeGroup.returnOrders.length > 0 && (
                <>
                  {' (linked to '}
                  {props.fulfillment.exchangeGroup.returnOrders.map(
                    (ro, index) => (
                      <Fragment key={ro.key}>
                        {index > 0 && ', '}
                        <Link
                          to="/return-orders/$returnOrderKey"
                          params={{ returnOrderKey: ro.key }}
                          target="_blank"
                        >
                          {ro.returnOrderNumber}
                        </Link>
                      </Fragment>
                    )
                  )}
                  {')'}
                </>
              )}
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage(null)}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        <FulfillmentOrderForm
          key={formVersion}
          fulfillment={props.fulfillment}
          initialProjectID={props.initialProjectID}
          initialFormData={props.initialFormData}
          preFillItems={props.preFillItems}
          orgDefaultOriginLocation={user.organization.defaultOriginLocation}
          onSubmit={handleFormSubmit}
          onProjectContextBlockedChange={setIsProjectContextBlocked}
          formId={FORM_ID}
          fileUploadProps={
            isEditMode
              ? undefined
              : {
                  files: fileUploads.files,
                  onFilesSelected: fileUploads.addFiles,
                  onRemoveFile: fileUploads.removeFile,
                  disabled: isPending,
                }
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          startIcon={
            isPending || fileUploads.isAnyUploading ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {isEditMode ? 'Save changes' : 'Create fulfillment order'}
        </Button>
      </DialogActions>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          setErrorMessage(null);
          createMutation.reset();
          updateMutation.reset();
        }}
        message={
          isEditMode
            ? 'An error occurred updating the fulfillment order.'
            : 'An error occurred creating the fulfillment order.'
        }
      />
    </Dialog>
  );
}
